import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'

import React, { useContext, useEffect } from 'react'
import { MapRenderContext } from 'src/contexts/MapRenderContext'
import { initPlanLayer } from 'src/lib/layers/planLayer'
import { fetchVehicleSvg, ensureVehicleLayersOnTop } from 'src/lib/util'
import { useMapTheme } from 'src/hooks/useMapTheme'

const lightStyle = new URL('/liberty-light.json', import.meta.url).href
const darkStyle = new URL('/liberty-dark.json', import.meta.url).href

function getMapStyle() {
    const stored = localStorage.getItem('mui-mode')
    if (stored === 'dark') return darkStyle
    if (stored === 'light') return lightStyle
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? darkStyle : lightStyle
}

export { lightStyle, darkStyle, getMapStyle }

export async function setupMap(map: maplibregl.Map) {
    const poiLayers = [
        'poi_r20', 'poi_r7', 'poi_r1', 'poi_transit',
    ]

    for (const layerId of poiLayers) {
        if (map.getLayer(layerId)) {
            map.setFilter(layerId, [
                'all',
                ['==', '$type', 'Point'],
                ['!=', 'class', 'bus'],
                ['!=', 'class', 'rail'],
                ['!=', 'class', 'railway'],
                ['!=', 'class', 'parking'],
            ])
        }
    }

    if (map.getLayer('poi_transit')) {
        map.setLayoutProperty('poi_transit', 'visibility', 'none')
    }

    map.addSprite('ojpp', window.location.origin + import.meta.env.VITE_BASE_PATH + '/sprites/sprites', { validate: false })

    // Fetch and cache SVG template for vehicle rendering
    await fetchVehicleSvg()

    initPlanLayer(map)

    // Ensure vehicle layers are always on top
    ensureVehicleLayersOnTop(map)

    const filterOptions = JSON.parse(localStorage.getItem('mapFilterOptions') || '{}')
    if (filterOptions && map.getStyle() && map.getStyle().layers) {
        for (const layer of map.getStyle().layers) {
            for (const key in filterOptions) {
                if (layer.id.includes(key.toLowerCase())) {
                    map.setLayoutProperty(layer.id, 'visibility', filterOptions[key] ? 'visible' : 'none')
                }
            }
        }
    }
}

const Map: React.FC = () => {
    const { map: mapRef, mapRef: containerRef, setMapLoaded } = useContext(MapRenderContext)

    useMapTheme()

    useEffect(() => {
        if (!mapRef || !containerRef || !containerRef.current) return
        if (mapRef.current) return

        const defaultCenter: [number, number] = [14.505751, 46.056946]

        mapRef.current = new maplibregl.Map({
            container: containerRef.current,
            style: getMapStyle(),
            center: defaultCenter,
            zoom: 11,
            minZoom: 6.5,
            renderWorldCopies: false,
            hash: false,
            attributionControl: false,
        })

        window.map = mapRef.current

        mapRef.current.addControl(new maplibregl.AttributionControl({
            compact: true,
        }), 'bottom-left')

        mapRef.current.addControl(new maplibregl.NavigationControl({
            showZoom: false,
            showCompass: true,
            visualizePitch: true,
        }), 'top-right')

        mapRef.current.addControl(new maplibregl.GeolocateControl({
            positionOptions: {
                enableHighAccuracy: true,
            },
            trackUserLocation: true,
            showUserLocation: true,
        }), 'bottom-right')

        mapRef.current.on('styledata', () => {
            if (!mapRef.current!.getStyle() || !mapRef.current!.getStyle().layers) return

            const filterOptions = JSON.parse(localStorage.getItem('mapFilterOptions') || '{}')
            if (!filterOptions) return

            for (const layer of mapRef.current!.getStyle().layers) {
                for (const key in filterOptions) {
                    if (layer.id.includes(key.toLowerCase())) {
                        mapRef.current!.setLayoutProperty(layer.id, 'visibility', filterOptions[key] ? 'visible' : 'none')
                    }
                }
            }
        })

        mapRef.current.once('load', async () => {
            await setupMap(mapRef.current!)
            setMapLoaded(true)
        })

        const basePath = import.meta.env.VITE_BASE_PATH || ''
        const normalizedBasePath = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath
        let appPath = window.location.pathname

        if (normalizedBasePath && appPath.startsWith(normalizedBasePath)) {
            appPath = appPath.slice(normalizedBasePath.length) || '/'
        }

        if (appPath === '/') {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const userCenter: [number, number] = [position.coords.longitude, position.coords.latitude]
                    if (mapRef.current) {
                        mapRef.current.flyTo({
                            center: userCenter,
                            zoom: 13,
                            duration: 2000,
                        })
                    }
                },
                (error) => {
                    console.error('Error getting location', error)
                },
            )
        }
    }, [mapRef, containerRef, setMapLoaded])

    return (
        <>
            <div id="map-container" ref={containerRef} style={{ height: '100%', width: '100%' }} />
        </>
    )
}

export default Map
